import { useMemo, useState } from 'react'

import { extractManufacturers, filterColors } from './colorFilter'
import { type ColorOption, type ColorTone, type Manufacturer } from './colorOptions'

/**
 * `selectedMaker` の状態型.
 * - `Manufacturer`（文字列）: 単一メーカーに絞り込み中
 * - `null`: 全メーカーを対象（初期値。トーンフィルタと同じ意味論）
 */
export type SelectedMaker = Manufacturer | null

/**
 * メーカー / カラートーン / 検索ワードによる色の絞り込み状態を集約するフック.
 *
 * ColorPalette（色指定パレット）と PresetEditor の左右ペインで共通利用する.
 * フィルタ UI（ColorFilterControls）は本フックの戻り値を props で受け取る.
 *
 * メーカーフィルタはトーンフィルタと同じ意味論を持つ: 初期値は `null`（= すべて）で、
 * チップをクリックすると選択、選択中のチップを再クリックすると解除（`null` に戻る）.
 */
export const useColorFilter = (colors: ColorOption[]) => {
  const makers = useMemo(() => extractManufacturers(colors), [colors])

  // ユーザーがチップで押した「生の値」. 初期値は null（全メーカー）.
  // 外へは出さない（下の effectiveMaker だけを公開する）.
  const [rawSelectedMaker, setRawSelectedMaker] = useState<SelectedMaker>(null)

  // ---- makers が差し替わった時の調整（2 段構え。どちらも必要）----
  //
  // (1) render 中の state 調整（React 公式パターン: 前回の props を state に持ち、
  //     render 中に変化を検出して setState する）. 同一 render pass 内で即座に
  //     コンポーネントが再実行されるため **ブラウザの paint を挟まない**.
  //     無効になった生の選択値をここで破棄することで、同じメーカーが再出現しても
  //     ユーザー操作なしにフィルタが復活する（ゾンビ復活）ことを防ぐ.
  //     実害例: CompanyColorPresetEditor は左右ペインの色が双方向に移動するため、
  //     「メーカー M で絞る → M の色を全部追加（チップ消滅）→ 1 色戻す」で
  //     右ペインが突然 1 件に collapse する.
  //
  //     useEffect + setState でやってはならない: 「無効なメーカーのまま filterColors が
  //     走る中間フレーム」が commit され、その瞬間 0 件表示になる（フィルタが壊れて見える）.
  const [makersSnapshot, setMakersSnapshot] = useState<Manufacturer[]>(makers)
  if (makersSnapshot !== makers) {
    setMakersSnapshot(makers)
    if (rawSelectedMaker !== null && !makers.includes(rawSelectedMaker)) {
      setRawSelectedMaker(null)
    }
  }

  // (2) 実効値の導出（保険）. (1) の調整が反映される前の 1 pass や、
  //     makers の参照が安定していないケースでも「フィルタ UI のハイライト
  //     （selectedMaker）と実際の絞り込みが常に同じ値を参照する」ことを保証する.
  const effectiveMaker: SelectedMaker =
    rawSelectedMaker !== null && makers.includes(rawSelectedMaker) ? rawSelectedMaker : null

  // トーンは colors から導出せず固定 enum（colorTones 12 種）を常時描画するため、
  // 「新しい色集合に存在しないトーン」でもチップは出続け、選択表示と絞り込みは一致する
  // （結果 0 件になり得るが、それは「該当なし」の正しい表示）. よって導出化は不要.
  const [selectedTone, setSelectedTone] = useState<ColorTone | null>(null)
  const [searchTerm, setSearchTerm] = useState<string>('')

  const filteredColors = useMemo(
    () =>
      filterColors(colors, {
        // null は filterColors 側で「全メーカー対象」として扱われる.
        manufacturer: effectiveMaker,
        tone: selectedTone,
        searchTerm,
      }),
    [colors, effectiveMaker, selectedTone, searchTerm],
  )

  const toggleMaker = (maker: Manufacturer) => {
    // functional updater（同一バッチ内の連続クリックを取りこぼさない）.
    // 比較は実効値基準（無効化された生の値は「未選択」とみなす）.
    setRawSelectedMaker(prev => {
      const prevEffective = prev !== null && makers.includes(prev) ? prev : null
      return prevEffective === maker ? null : maker
    })
  }

  /** メーカー絞り込みを解除して全メーカー対象に戻す（「すべて」チップ用）. */
  const clearMaker = () => {
    setRawSelectedMaker(null)
  }

  const toggleTone = (tone: ColorTone) => {
    setSelectedTone(prev => (prev === tone ? null : tone))
  }

  return {
    makers,
    // 生の rawSelectedMaker とその setter は公開しない.
    // 生 setter を公開すると「存在しないメーカーを set しても黙って無視される」という
    // 非対称な API（selectedMaker は実効値 / setter は生値）になるため、
    // 外部に必要な操作だけを toggleMaker / clearMaker として出す.
    selectedMaker: effectiveMaker,
    toggleMaker,
    clearMaker,
    selectedTone,
    toggleTone,
    searchTerm,
    setSearchTerm,
    filteredColors,
    totalColorCount: colors.length,
  }
}

export type ColorFilterState = ReturnType<typeof useColorFilter>
